/**
 * SpiderChart Component
 * 
 * Componente React que renderiza un gráfico de araña (radar chart) para mostrar 
 * los resultados de la evaluación DISC (Dominancia, Influencia, Constancia, Cumplimiento).
 * 

 * @author Ana Lizcano
 * @version 1.0.0
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Registrar los componentes necesarios de Chart.js para el gráfico radar
ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

/**
 * Componente funcional SpiderChart
 * 
 * Renderiza un gráfico de araña interactivo que visualiza los resultados 
 * de una evaluación DISC con datos reales del backend.
 * 
 * @param {Object} props - Propiedades del componente
 * @param {string} props.title - Título que se mostrará encima del gráfico
 * @param {Object} props.discData - Datos DISC del backend
 * @returns {JSX.Element} Elemento JSX del gráfico de araña
 */
const SpiderChart = React.memo(function SpiderChart({ title = "Perfil Candidato", discData = null, size = "large" }) {
  /**
   * Datos por defecto si no se proporcionan datos del backend
   * 
   * Cada factor se mide en una escala de 0-100:
   * - 0-25: Nivel bajo
   * - 26-50: Nivel medio-bajo  
   * - 51-75: Nivel medio-alto
   * - 76-100: Nivel alto
   */
  const defaultResults = {
    D: 0, // Dominancia - Decisivo, Determinado, Competitivo
    I: 0, // Influencia - Persuasivo, Sociable, Optimista  
    S: 0, // Constancia - Paciente, Leal, Considerado
    C: 0  // Cumplimiento - Cauteloso, Preciso, Disciplinado
  };

  // Usar datos reales del backend o valores por defecto
  const testResults = discData || defaultResults;

  // Configuración de tamaños y estilos
  const sizeConfig = {
    small: { 
      width: 180, 
      height: 180, 
      chartSize: 140,
      titleSize: 'body2',
      fontSize: 8,
      legendFontSize: 9,
      tickFontSize: 8,
      pointRadius: 4,
      pointHoverRadius: 6,
      borderWidth: 2,
      padding: '10px',
      stepSize: 25
    },
    medium: { 
      width: 350, 
      height: 350, 
      chartSize: 310,
      titleSize: 'h6',
      fontSize: 11,
      legendFontSize: 12,
      tickFontSize: 10,
      pointRadius: 6,
      pointHoverRadius: 8,
      borderWidth: 2,
      padding: '15px',
      stepSize: 20
    },
    large: { 
      width: 420, 
      height: 420, 
      chartSize: 380,
      titleSize: 'h6',
      fontSize: 12,
      legendFontSize: 13,
      tickFontSize: 11,
      pointRadius: 8,
      pointHoverRadius: 12,
      borderWidth: 3,
      padding: '20px',
      stepSize: 20
    }
  };
  
  const currentSize = sizeConfig[size] || sizeConfig.large;

  // Memoize chart data to prevent unnecessary re-renders
  const data = useMemo(() => ({
    // Etiquetas para cada eje del gráfico radar (4 factores DISC)
    // Orden: D, I, S, C para coincidir con la imagen de referencia
    labels: [
      'D (Dominancia)',
      'I (Influencia)', 
      'S (Constancia)',
      'C (Cumplimiento)'
    ],
    datasets: [
      {
        label: 'Puntuación DISC',
        data: [testResults.D, testResults.I, testResults.S, testResults.C],
        // Styling del área del gráfico
        backgroundColor: 'rgba(102, 126, 234, 0.15)', // Relleno semi-transparente
        borderColor: '#667eea', // Color del borde principal
        borderWidth: currentSize.borderWidth,
        // Styling de los puntos en cada vértice
        pointBackgroundColor: '#667eea',
        pointBorderColor: '#ffffff',
        pointBorderWidth: currentSize.borderWidth,
        // Styling de los puntos al hacer hover
        pointHoverBackgroundColor: '#ffffff',
        pointHoverBorderColor: '#667eea',
        pointHoverBorderWidth: currentSize.borderWidth + 1,
        pointRadius: currentSize.pointRadius,
        pointHoverRadius: currentSize.pointHoverRadius,
        fill: true, // Rellenar el área del polígono
        tension: 0.3, // Suavidad de las líneas
      },
    ]
  }), [testResults.D, testResults.I, testResults.S, testResults.C]);

  // Memoize chart options for better performance
  const options = useMemo(() => ({
    // Configuración de responsividad
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 1, // Mantener proporción cuadrada
    animation: false, // Desactivar animaciones para mejor rendimiento
    
    // Configuración de plugins
    plugins: {
      // Configuración de la leyenda
      legend: {
        position: 'bottom',
        labels: {
          font: {
            size: currentSize.legendFontSize,
            family: 'Arial, sans-serif',
            weight: '500'
          },
          color: '#344767',
          padding: size === 'small' ? 8 : 25,
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      // Configuración de tooltips (información al hover)
      tooltip: {
        backgroundColor: 'rgba(52, 71, 103, 0.95)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#667eea',
        borderWidth: 2,
        cornerRadius: 8,
        padding: 12,
        displayColors: true,
        // Callbacks para personalizar el contenido del tooltip
        callbacks: {
          title: function(context) {
            return `Factor ${context[0].label}`;
          },
          label: function(context) {
            return `Puntuación: ${context.parsed.r}%`;
          }
        }
      }
    },
    
    // Configuración de escalas radiales
    scales: {
      r: {
        // Líneas angulares que van del centro hacia los vértices
        angleLines: {
          display: true,
          color: 'rgba(102, 126, 234, 0.15)',
          lineWidth: 1
        },
        // Rango de valores del gráfico
        suggestedMin: 0,
        suggestedMax: 100,
        // Configuración de las marcas en los anillos concéntricos
        ticks: {
          stepSize: currentSize.stepSize,
          font: {
            size: currentSize.tickFontSize,
            weight: '500'
          },
          color: '#7b809a',
          backdropColor: 'rgba(255, 255, 255, 0.8)',
          backdropPadding: size === 'small' ? 2 : 4,
          showLabelBackdrop: true
        },
        // Líneas de la grilla (anillos concéntricos)
        grid: {
          color: 'rgba(102, 126, 234, 0.2)',
          lineWidth: 1.5
        },
        // Etiquetas de los puntos (nombres de los factores DISC)
        pointLabels: {
          font: {
            size: currentSize.fontSize,
            weight: 'bold',
            family: 'Arial, sans-serif'
          },
          color: '#344767',
          padding: size === 'small' ? 4 : 8
        }
      }
    },
    
    // Configuración de elementos gráficos
    elements: {
      line: {
        tension: 0.1 // Suavidad de las líneas conectoras
      }
    }
  }), []);

  return (
    <MDBox>
      <MDTypography variant={currentSize.titleSize} fontWeight="medium" color="dark" mb={size === 'small' ? 1 : 2} textAlign="center">
        {title}
      </MDTypography>
      <MDBox
        sx={{
          height: `${currentSize.height}px`,
          width: `${currentSize.width}px`,
          maxWidth: '100%',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(248, 249, 250, 0.8)',
          borderRadius: size === 'small' ? '8px' : '16px',
          boxShadow: size === 'small' ? '0 2px 10px rgba(102, 126, 234, 0.1)' : '0 4px 20px rgba(102, 126, 234, 0.1)',
          padding: currentSize.padding
        }}
      >
        <Radar data={data} options={options} width={currentSize.chartSize} height={currentSize.chartSize} />
      </MDBox>
      
      {/* Interpretación de resultados - Solo mostrar en tamaños medium y large */}
      {size !== 'small' && (
        <MDBox mt={3}>
          <MDTypography variant="body2" color="dark" mb={2} fontWeight="medium">
            Interpretación de Resultados:
          </MDTypography>
          <MDBox ml={2}>
            <MDTypography variant="body2" mb={1}>
              Evidencia la manera en que la persona se expresa de forma natural en su vida cotidiana. Es el estilo más auténtico y estable de su comportamiento, aquel que aflora en sus interacciones diarias y que constituye su huella característica al relacionarse con los demás y con su entorno.


            </MDTypography>
          </MDBox>
        </MDBox>
      )}
    </MDBox>
  );
});

/**
 * Validación de PropTypes para el componente SpiderChart
 * 
 * Define las propiedades esperadas y sus tipos para validación en desarrollo.
 * Ayuda a detectar errores tempranamente y mejora la experiencia de desarrollo.
 */
SpiderChart.propTypes = {
  title: PropTypes.string, // Título opcional del gráfico
  discData: PropTypes.object, // Datos DISC del backend
  size: PropTypes.oneOf(['small', 'medium', 'large']), // Tamaño del gráfico
};

/**
 * Exportación por defecto del componente SpiderChart
 * 
 * Permite importar el componente usando:
 * import SpiderChart from 'components/SpiderChart';
 */
export default SpiderChart;